import { Col, Row } from 'antd';
import classNames from 'classnames';
import { startsWith } from 'lodash-es';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Prompt, matchPath, useHistory, useRouteMatch } from 'react-router';

import routes from '../app/routes';
import Whitespace from '../components/Whitespace';
import { DataStateIndicatorGuard } from '../components/data/dataStateHandlers';
import { DialogsParentContext } from '../components/dialogs/DialogsParentContext';
import { OverrideSettingsContextProvider } from '../components/domainSpecific/settingsElements';
import { FormSanitizationProvider } from '../components/forms/FormSanitizationContext';
import { DynamicFormDependenciesProvider } from '../components/forms/dynamic/dynamicFormDependencies';
import { FormInterceptorContext } from '../components/forms/forms';
import { withPageGuard } from '../components/guards/PageGuard';
import { newShipmentGuard } from '../components/guards/pageGuards';
import { InnerContentScrollbars } from '../components/layout/InnerContentScrollbars';
import { FlexCol, TitleWithExtra } from '../components/layout/layoutElements';
import {
  StepsWithContent,
  isFirstStep,
  isLastStep,
} from '../components/nav/StepsWithContent';
import {
  AccountSelectionDialog,
  NewShipmentSubmitDialog,
  useNewShipmentSubmit,
} from '../dialogs/shipments/newShipmentDialogs';
import NewShipmentFormSchemaProvider from '../forms/shipments/newShipment/NewShipmentFormSchemaProvider';
import { SavedShipmentProvider } from '../forms/shipments/newShipment/SavedShipmentProvider';
import { NEW_SHIPMENT_STEPS } from '../forms/shipments/newShipment/newShipmentConstants';
import {
  CancelShipmentLink,
  NewShipmentBottomControls,
  NewShipmentFormButtons,
} from '../forms/shipments/newShipment/newShipmentElements';
import {
  isSavedShipmentModeActive,
  shouldForceTbdServiceType,
} from '../forms/shipments/newShipment/savedShipmentsUtils';
import useNewShipmentAsyncConfig from '../forms/shipments/newShipment/useNewShipmentAsyncConfig';
import useNewShipmentFormState from '../forms/shipments/newShipment/useNewShipmentFormState';
import useNewShipmentNiceUrl, {
  NewShipmentSubRoute,
} from '../forms/shipments/newShipment/useNewShipmentNiceUrl';
import useNewShipmentStateUpdaters from '../forms/shipments/newShipment/useNewShipmentStateUpdaters';
import useNewShipmentSteps from '../forms/shipments/newShipment/useNewShipmentSteps';
import useSubmitNewShipmentStep from '../forms/shipments/newShipment/useSubmitNewShipmentStep';
import { useShipmentTemplateChangeDetectionInterceptors } from '../forms/shipments/shipmentTemplates/ShipmentTemplateChangedDialog';
import ShipmentTemplatesCard from '../forms/shipments/shipmentTemplates/ShipmentTemplatesCard';
import { useAccounts } from '../hooks/data/auth';
import { cssVariables } from '../styles/cssVariables';
import BaseLoggedPage from '../templates/BaseLoggedPage';
import { pxToNumber } from '../utils/cssUtils';
import ShipmentOrderPreviewCard from '../widgets/shipments/ShipmentOrderPreviewCard';
import { PageExitDialogType } from './nav/PageExitDialog';

function NewShipmentPageWrapper({ account, title, children }) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const computedIsDialogOpen = isDialogOpen || !account;
  const isPartnerAccess = account?.partner;

  return (
    <BaseLoggedPage
      id="NewShipmentPage"
      className={classNames('height-extending-error', {
        'has-dialog': computedIsDialogOpen,
      })}
    >
      <DialogsParentContext.Provider
        value={{ isDialogOpen: computedIsDialogOpen, setDialogOpen }}
      >
        <FlexCol className="Flex1">
          {isPartnerAccess && (
            <div className="NewShipmentPage__PartnerAccessTopBar">
              <FormattedMessage id="book.newShipment.partnerAccess" />
              <Whitespace />|<Whitespace />
              {account.name}
              <Whitespace />
              {account.number}
            </div>
          )}
          {title}
          <InnerContentScrollbars>{children}</InnerContentScrollbars>
        </FlexCol>
      </DialogsParentContext.Provider>
    </BaseLoggedPage>
  );
}

function NewShipmentExitPrompt({ match }) {
  const { location } = useHistory();
  return (
    <Prompt
      when={
        // Don't open prompt when in account selection - there is nothing to be lost yet
        `${match.path}/${NewShipmentSubRoute.ACCOUNT_SELECTION}` !==
        location.pathname
      }
      message={({ pathname }) =>
        startsWith(pathname, routes.book.newShipment) || // New shipment subpages are allowed
        matchPath(pathname, { path: routes.shipmentDetail }) // Redirect to detail in the end is allowed
          ? true
          : PageExitDialogType.CANCEL_SHIPMENT
      }
    />
  );
}

function NewShipmentProviders({
  values,
  account,
  setAccount,
  unitSystem,
  shipmentTemplateChangeDetection,
  children,
}) {
  const accountsResult = useAccounts();

  const savedShipmentModeActive = isSavedShipmentModeActive(values);
  const forceTbdServiceType = shouldForceTbdServiceType(values);

  return (
    <FormInterceptorContext.Provider
      value={{
        onBlur: shipmentTemplateChangeDetection.onBlur,
        onKeyDown: shipmentTemplateChangeDetection.onKeyDown,
      }}
    >
      <NewShipmentFormSchemaProvider account={account}>
        <DynamicFormDependenciesProvider values={values}>
          <SavedShipmentProvider
            active={savedShipmentModeActive}
            forceTbdServiceType={forceTbdServiceType}
          >
            <OverrideSettingsContextProvider unitSystem={unitSystem}>
              <FormSanitizationProvider>
                <DataStateIndicatorGuard
                  queryResult={{
                    ...accountsResult,
                    data: accountsResult.customerAccounts,
                  }}
                >
                  {customerAccounts => (
                    <>
                      {children}
                      <AccountSelectionDialog
                        value={account}
                        onChange={setAccount}
                        customerAccounts={customerAccounts}
                      />
                    </>
                  )}
                </DataStateIndicatorGuard>
              </FormSanitizationProvider>
            </OverrideSettingsContextProvider>
          </SavedShipmentProvider>
        </DynamicFormDependenciesProvider>
      </NewShipmentFormSchemaProvider>
    </FormInterceptorContext.Provider>
  );
}

function NewShipmentPageInner() {
  const match = useRouteMatch();

  const {
    forms,
    refreshValues,
    account,
    setAccount,
    unitSystem,
    setUnitSystem,
    values,
    extractValues,
  } = useNewShipmentFormState();

  const {
    submit,
    dialogProps: submitDialogProps,
    isConfirming,
  } = useNewShipmentSubmit();

  const [activeTemplateCounter, setActiveTemplateCounter] = useState(0);
  const {
    stepsProps,
    currentIndex,
    goToNext,
    goToPrev,
    step,
    resetStep,
  } = useNewShipmentSteps({
    forms,
    refreshValues,
    activeTemplateCounter,
    steps: NEW_SHIPMENT_STEPS,
    disableUnvisitedSteps: true,
  });

  const { afterApplyTemplate } = useNewShipmentStateUpdaters({
    forms,
    refreshValues,
    setUnitSystem,
  });
  const afterCancelTemplate = useCallback(() => {
    afterApplyTemplate();
    resetStep();
  }, [afterApplyTemplate, resetStep]);
  const onActiveTemplateChange = useCallback(({ fromForm }) => {
    // If form is saved as a template, it's not considered a change of active template
    if (!fromForm) {
      setActiveTemplateCounter(old => old + 1);
    }
  }, []);

  useNewShipmentNiceUrl({
    baseUrl: match.url,
    account,
    isConfirming,
    step,
  });

  const savedShipmentModeActive = isSavedShipmentModeActive(values);
  const {
    accountDetailResult,
    isQuoteEnabled: isQuoteEnabledConfig,
  } = useNewShipmentAsyncConfig({
    account,
  });
  const isQuoteEnabled = isQuoteEnabledConfig && !savedShipmentModeActive;

  const submitStep = useSubmitNewShipmentStep({
    forms,
    stepsProps,
    currentIndex,
    submit,
    extractValues,
    isQuoteEnabled,
    goToNext,
    steps: NEW_SHIPMENT_STEPS,
  });

  const {
    props: shipmentTemplateChangeDetectionProps,
    ...shipmentTemplateChangeDetection
  } = useShipmentTemplateChangeDetectionInterceptors();

  return (
    <NewShipmentProviders
      account={account}
      setAccount={setAccount}
      unitSystem={unitSystem}
      values={values}
      shipmentTemplateChangeDetection={shipmentTemplateChangeDetection}
    >
      <NewShipmentExitPrompt match={match} />
      <NewShipmentPageWrapper
        account={account}
        title={
          <TitleWithExtra
            title={
              <>
                <FormattedMessage id="book.newShipment.title" />
                <CancelShipmentLink className="hide-md-and-smaller" />
              </>
            }
            rightColClassName="hide-md-and-smaller"
          >
            <DataStateIndicatorGuard queryResult={accountDetailResult}>
              {() => (
                <div className="NewShipmentTopControls">
                  <NewShipmentFormButtons
                    onSubmit={submitStep}
                    onBack={goToPrev}
                    isFirstStep={isFirstStep(stepsProps)}
                    isLastStep={isLastStep(stepsProps)}
                    isQuoteEnabled={isQuoteEnabled}
                  />
                </div>
              )}
            </DataStateIndicatorGuard>
          </TitleWithExtra>
        }
      >
        <Row
          className="flex-nowrap"
          gutter={pxToNumber(cssVariables.spaceNorm2)}
        >
          <Col className="Flex1 spaces-vert-norm1_5">
            <ShipmentTemplatesCard
              forms={forms}
              values={values}
              afterApply={afterApplyTemplate}
              afterCancel={afterCancelTemplate}
              changeDetectionProps={shipmentTemplateChangeDetectionProps}
              onActiveItemChange={onActiveTemplateChange}
            />
            <StepsWithContent
              {...stepsProps}
              className="spaces-vert-norm1_5"
              stepsClassName="labels-in-middle"
              segmentedControlClassName="margin-hor-norm"
              clickable
            />
          </Col>
          <Col className="hide-md-and-smaller">
            <ShipmentOrderPreviewCard
              key={activeTemplateCounter}
              account={account}
              values={values}
              stepIndex={currentIndex}
            />
          </Col>
        </Row>
        <DataStateIndicatorGuard queryResult={accountDetailResult}>
          {() => (
            <>
              <NewShipmentBottomControls
                submitStep={submitStep}
                stepsProps={stepsProps}
                isQuoteEnabled={isQuoteEnabled}
              />
              <NewShipmentSubmitDialog
                {...submitDialogProps}
                isQuoteEnabled={isQuoteEnabled}
                savedShipmentModeActive={savedShipmentModeActive}
              />
            </>
          )}
        </DataStateIndicatorGuard>
      </NewShipmentPageWrapper>
    </NewShipmentProviders>
  );
}

const NewShipmentPage = withPageGuard(newShipmentGuard)(NewShipmentPageInner);
export default NewShipmentPage;
